import {defineConfig, type Config} from 'sanity'
import {structureTool} from 'sanity/structure'
import {visionTool} from '@sanity/vision'
import {schema, structure} from './schemaTypes/index'
import resolveUrl, {resolvePostUrlFromPreview} from '@/lib/resolveUrl'
import {SanityPageBase} from './types/Sanity'
import {defineLocations, presentationTool} from 'sanity/presentation'
import {SANITY_STUDIO_WEB_URL} from './lib/env'
import {dataset, datasetStage, projectId} from './lib/dataset'
import {media} from 'sanity-plugin-media'
import {defaultDocumentNodeResolver} from './lib/defaultDocumentNodeResolver'
import {cacheDashboard} from './dashboard/dashboard'
import {ChangeCategoryAction} from './actions/ChangeCategoryAction'

const singletonTypes = ['site']

const config: Config = defineConfig({
  name: 'silversea-blog',
  title: `Silversea Blog (${datasetStage})`,

  projectId: projectId,
  dataset: dataset,

  auth: {
    loginMethod: 'token',
  },

  plugins: [
    structureTool({structure, defaultDocumentNode: defaultDocumentNodeResolver}),
    media(),
    visionTool(),
    cacheDashboard(),
    presentationTool({
      previewUrl: {
        origin: SANITY_STUDIO_WEB_URL,
        previewMode: {
          enable: '/blog/api/draft-mode/enable',
        },
      },
      resolve: {
        locations: {
          page: defineLocations({
            select: {
              title: 'title',
              slug: 'metadata.slug.current',
            },
            resolve: (doc) => ({
              locations: [
                {
                  title: doc?.title,
                  href: doc?.slug ? (doc.slug === 'index' ? '/' : `/${doc.slug}`) : '/',
                },
              ],
            }),
          }),
          'blog.post': defineLocations({
            select: {
              title: 'metadata.title',
              slug: 'metadata.slug.current',
              mainCategorySlug: 'mainCategory.metadata.slug.current',
              clusterSlug: 'mainCategory.cluster.metadata.slug.current',
            },
            resolve: (doc) => ({
              locations: [
                {
                  title: doc?.title,
                  href: resolvePostUrlFromPreview({
                    slug: doc?.slug,
                    mainCategorySlug: doc?.mainCategorySlug,
                    clusterSlug: doc?.clusterSlug,
                  }),
                },
              ],
            }),
          }),
        },
      },
    }),
  ],

  schema: {
    types: schema.types,
    templates: (templates) => {
      const templatesWithoutSingleton = templates.filter(
        ({schemaType}) => !singletonTypes.includes(schemaType),
      )

      const categoryChild = {
        id: 'category-child',
        title: 'Category: Child',
        schemaType: 'blog.category',
        parameters: [{name: `parentId`, title: `Parent ID`, type: `string`}],
        // This value will be passed-in from desk structure
        value: ({parentId}: {parentId: string}) => ({
          parent: {_type: 'reference', _ref: parentId},
        }),
      }

      const categoryInCluster = {
        id: 'category-in-cluster',
        title: 'Category in Cluster',
        schemaType: 'blog.category',
        parameters: [{name: 'clusterId', title: 'Cluster ID', type: 'string'}],
        // This value will be passed-in from desk structure
        value: (params: {clusterId?: string}) => ({
          cluster: params.clusterId ? {_type: 'reference', _ref: params.clusterId} : undefined,
        }),
      }

      return [...templatesWithoutSingleton, categoryChild, categoryInCluster]
    },
  },
  document: {
    productionUrl: async (prev, {document}) => {
      if (['page', 'blog.post'].includes(document?._type)) {
        return resolveUrl(document as SanityPageBase, {base: true})
      }
      return prev
    },

    actions: (input, {schemaType}) => {
      if (singletonTypes.includes(schemaType)) {
        return input.filter(
          ({action}) => action && ['publish', 'discardChanges', 'restore'].includes(action),
        )
      }

      // Add custom action for blog.category
      if (schemaType === 'blog.category') {
        return [...input, ChangeCategoryAction]
      }

      // Add custom action for blog.category-cluster
      if (schemaType === 'blog.category-cluster') {
        return [...input, ChangeCategoryAction]
      }

      return input
    },
  },
})

export default config
